import React, { useEffect, useReducer, useMemo } from 'react';
import api from '../../../utils/api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { showToast } from '../../../Components/ToastNotifications';

// --- Reducer untuk state management ---
const initialState = {
  loading: false, // untuk submit
  fetching: true, // untuk fetch awal
  error: null,
  mentess: [],
  successMessage: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, fetching: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, fetching: false, mentess: action.payload };
    case 'FETCH_ERROR':
      return { ...state, fetching: false, error: action.payload };
    case 'UPDATE_EVALUATION':
      return {
        ...state,
        mentess: state.mentess.map((item, i) =>
          i === action.key ? { ...item, evaluation: action.value } : item,
        ),
      };
    case 'UPDATE_NOTE':
      return {
        ...state,
        mentess: state.mentess.map((item, i) =>
          i === action.key ? { ...item, note: action.value } : item,
        ),
      };
    case 'SAVE_START':
      return { ...state, loading: true, error: null, successMessage: null };
    case 'SAVE_SUCCESS':
      return { ...state, loading: false, successMessage: 'Berhasil disimpan!' };
    case 'SAVE_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

// --- Komponen kecil untuk KPI Softskill ---
const KpiSoftField = ({ value, index, onChangeEvaluation, onChangeNote, maxVal }) => (
  <div key={index} className='card border-0 shadow-sm rounded-4 mb-4 overflow-hidden'>
    <div className='card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center'>
      <div className='d-flex align-items-center gap-2'>
        <span className='badge bg-primary rounded-circle d-flex align-items-center justify-content-center' style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>
          {index + 1}
        </span>
        <h6 className='fw-bold text-dark mb-0'>Key Performance Indicator (Soft Skill)</h6>
      </div>
      <div className='d-flex align-items-center gap-2' style={{ maxWidth: '160px' }}>
        <span className='small text-muted fw-medium text-nowrap'>Nilai (1-{maxVal}):</span>
        <input
          type='number'
          value={value.evaluation || ''}
          onChange={(e) => onChangeEvaluation(e.target.value, index)}
          onWheel={(e) => e.target.blur()}
          placeholder='0'
          className='form-control form-control-sm text-center fw-bold border-light-subtle rounded-pill'
          min={1}
          max={maxVal}
          required
          style={{ width: '65px' }}
        />
      </div>
    </div>
    <div className='card-body p-4'>
      <div className='mb-3 p-3 rounded-3 bg-light border border-light-subtle'>
        <p className='mb-0 text-secondary small fw-medium style-description' style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
          {value.classification}
        </p>
      </div>

      <div className='mb-0'>
        <label className='form-label small fw-bold text-dark d-flex align-items-center gap-1 mb-2'>
          <i className='bi bi-chat-right-text text-primary'></i>
          Catatan Evaluasi / Bukti Kinerja
        </label>
        <textarea
          rows={3}
          value={value.note || ''}
          onChange={(e) => onChangeNote(e.target.value, index)}
          placeholder='Tuliskan catatan detail atau bukti pencapaian KPI soft skill ini...'
          className='form-control border-light-subtle bg-light bg-opacity-25 shadow-none'
          style={{ fontSize: '0.9rem' }}
          required
        />
      </div>
    </div>
  </div>
);

const EvaluationSSAdd = () => {
  const location = useLocation();
  const { data } = location.state || {};
  const { id } = useParams();
  const navigate = useNavigate();

  const [state, dispatch] = useReducer(reducer, initialState);
  const maxVal = 100;
  const isAdmin = window.location.pathname.startsWith('/admin');

  // --- Fetch Data Softskill ---
  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        dispatch({ type: 'FETCH_START' });
        const result = await api.getFinalSoftSkillEvaluations({ id });
        dispatch({ type: 'FETCH_SUCCESS', payload: result });
      } catch (err) {
        dispatch({
          type: 'FETCH_ERROR',
          payload: 'Gagal memuat data softskill.',
        });
      }
    };
    fetchEvaluations();
  }, [id]);

  // --- Validation ---
  const isDisabled = useMemo(() => {
    if (!state.mentess?.length) return true;
    return state.mentess.some(
      (item) =>
        !item.evaluation ||
        item.evaluation < 1 ||
        item.evaluation > maxVal ||
        !item.note?.trim(),
    );
  }, [state.mentess, maxVal]);

  // --- Handler input ---
  const handleChangeEvaluation = (value, key) => {
    const num = parseInt(value, 10);
    if (num > maxVal) {
      showToast(`Nilai tidak boleh lebih dari ${maxVal}!`, 'danger');
      return;
    }
    dispatch({ type: 'UPDATE_EVALUATION', key, value: num });
  };

  const handleChangeNote = (value, key) => {
    dispatch({ type: 'UPDATE_NOTE', key, value });
  };

  // --- Submit Handler ---
  const handleSave = async () => {
    try {
      dispatch({ type: 'SAVE_START' });
      const payload = {
        finalEvaluationsId: parseInt(id),
        evaluates: state.mentess,
      };
      await api.updateFinalSoftSkillEvaluations({ id, payload });
      showToast('Berhasil disimpan!', 'success');
      dispatch({ type: 'SAVE_SUCCESS' });
      navigate(isAdmin ? `/admin/students/${id}/evaluations` : `/mentor/mentees/${id}/evaluations`);
    } catch (err) {
      showToast('Gagal menyimpan penilaian.', 'danger');
      dispatch({ type: 'SAVE_ERROR', payload: 'Gagal menyimpan penilaian.' });
    }
  };

  // --- UI utama ---
  return (
    <div className='container-fluid p-0 pb-5'>
      {/* Premium Header */}
      <div className='card border-0 rounded-4 mb-4 overflow-hidden shadow-sm'>
        <div className='card-body p-4'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
            <div className='d-flex align-items-center gap-3'>
              <button
                onClick={() => navigate(isAdmin ? `/admin/students/${id}/evaluations` : `/mentor/mentees/${id}/evaluations`)}
                className='btn btn-light rounded-circle d-flex align-items-center justify-content-center border transition-all hover-scale'
                style={{ width: '40px', height: '40px' }}
              >
                <i className='bi bi-arrow-left'></i>
              </button>
              <div>
                <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold small mb-1'>
                  Penilaian Soft Skill
                </span>
                <h1 className='h4 fw-bold mb-0 text-dark'>
                  {data?.mentessFirstName} {data?.mentessLastName}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-4'>
        {/* Left Column: KPI / Softskill Fields */}
        <div className='col-lg-8 order-lg-1 order-2'>
          {state.fetching ? (
            <div className='d-flex justify-content-center align-items-center py-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='d-grid gap-3'>
              {state.error && (
                <div className='alert alert-danger rounded-3 py-3 px-4 fw-medium d-flex align-items-center gap-2'>
                  <i className='bi bi-exclamation-triangle-fill'></i>
                  {state.error}
                </div>
              )}
              {state.successMessage && (
                <div className='alert alert-success rounded-3 py-3 px-4 fw-medium d-flex align-items-center gap-2'>
                  <i className='bi bi-check-circle-fill'></i>
                  {state.successMessage}
                </div>
              )}

              {state.mentess?.map((value, index) => (
                <KpiSoftField
                  key={index}
                  value={value}
                  index={index}
                  onChangeEvaluation={handleChangeEvaluation}
                  onChangeNote={handleChangeNote}
                  maxVal={maxVal}
                />
              ))}

              <div className='d-grid mt-2'>
                <button
                  disabled={isDisabled || state.loading}
                  className='btn btn-primary btn-lg fw-bold rounded-pill shadow d-flex align-items-center justify-content-center gap-2 hover-scale'
                  type='button'
                  onClick={handleSave}
                >
                  {state.loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Menyimpan Penilaian...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-save2'></i> Simpan Semua Penilaian
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Profil Mahasiswa */}
        <div className='col-lg-4 order-lg-2 order-1'>
          <div className='d-grid gap-4 sticky-top' style={{ top: '1.5rem', zIndex: 10 }}>
            {/* User Info Card */}
            <div className='card border-0 rounded-4 shadow-sm'>
              <div className='card-body p-4'>
                <h6 className='fw-bold text-dark mb-4 pb-2 border-bottom'>
                  Profil Mahasiswa
                </h6>
                <div className='row g-3'>
                  {data?.mentors && (
                    <div className='col-12 col-md-6 col-lg-12'>
                      <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                        <label className='small text-muted mb-1 d-block fw-medium'>
                          <i className='bi bi-person-badge me-2 text-primary'></i>
                          Mentor Pembimbing
                        </label>
                        <span className='fw-bold text-dark small'>
                          {data?.mentors?.firstName} {data?.mentors?.lastName}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-book me-2 text-primary'></i>
                        Jurusan
                      </label>
                      <span className='fw-semibold text-dark small'>
                        {data?.universities?.universitiesMajor || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-mortarboard me-2 text-primary'></i>
                        Universitas
                      </label>
                      <span className='fw-semibold text-dark small'>
                        {data?.universities?.universitiesName || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-briefcase me-2 text-primary'></i>
                        Divisi Penempatan
                      </label>
                      <div className='d-block mt-1'>
                        <span className='fw-semibold text-dark small'>
                          {data?.institutions?.divisionName || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .hover-scale {
          transition: transform 0.2s ease, background-color 0.2s ease;
        }
        .hover-scale:hover {
          transform: scale(1.02);
        }
        .style-description {
          font-family: inherit;
        }
      `}</style>
    </div>
  );
};

export default EvaluationSSAdd;
